import { createContext, useContext } from "react";

/**
 * Registro del trabajo asincrono pendiente dentro de un portal de exportacion.
 *
 * El PDF se arma serializando el DOM del portal (`serializeContainer`), y el layout
 * lo hace wkhtmltopdf del lado servidor: no hace falta esperar a que la pagina
 * "termine de pintarse", solo a que React haya commiteado el arbol con los datos
 * finales. Este registro es como se sabe cuando ocurrio eso.
 *
 * Antes esa señal era `requestAnimationFrame` + `debounce(800)` + `setTimeout(300)`,
 * y ese era el bug: **rAF se suspende por completo en una pestaña oculta** (los
 * timers solo se degradan). Si el usuario guardaba y cambiaba de pestaña, la señal
 * `DOC_READY` no se emitia nunca, el portal moria por timeout y el PDF no llegaba a
 * S3 — con lo cual el correo de facturacion electronica salia sin el adjunto.
 *
 * Por eso aqui NO se usa ningun timer. Lo unico que se degrada en una pestaña
 * oculta son `requestAnimationFrame` (suspendido) y `setTimeout`/`setInterval`
 * (throttled a 1/s, y a 1/min tras 5 minutos ocultos). Las promesas, los microtasks
 * y `MessageChannel` corren sin degradacion.
 *
 * Vive en su propio modulo (y no en `routePortal.tsx`) por la misma razon que
 * `exportPortal.ts`: ese importa `AppRouter`, y un componente de hoja como
 * `PrintPageHeader` no puede arrastrar el arbol de rutas completo solo para
 * registrar que esta resolviendo la URL de un logo.
 */

export type ExportReadiness = {
    /** Registra trabajo asincrono pendiente. Devuelve la funcion de liberacion. */
    begin: () => () => void,
    /** Resuelve cuando ya no queda trabajo pendiente. */
    whenSettled: () => Promise<void>,
}

/**
 * Cede el control hasta el proximo macrotask usando `MessageChannel`.
 *
 * **No cambiar esto por `setTimeout`.** Es el punto entero del arreglo: los timers
 * se throttlean en pestaña oculta y `MessageChannel` no. Es tambien el mecanismo que
 * usa el propio scheduler de React para sus efectos pasivos, justamente por eso, asi
 * que ceder por aqui garantiza que React alcanzo a vaciar los efectos pendientes.
 */
const yieldToTask = (): Promise<void> =>
    new Promise<void>((resolve) => {
        const channel = new MessageChannel();
        channel.port1.onmessage = () => {
            channel.port1.close();
            resolve();
        };
        channel.port2.postMessage(undefined);
    });

export const createExportReadiness = (): ExportReadiness => {
    let pending = 0;
    /** Esperas despertadas cuando el contador vuelve a cero. */
    const idleWaiters = new Set<() => void>();

    const begin = () => {
        pending++;
        let released = false;
        return () => {
            if (released) return;
            released = true;
            pending--;
            if (pending > 0) return;
            const waiters = Array.from(idleWaiters);
            idleWaiters.clear();
            waiters.forEach((wake) => wake());
        };
    };

    const whenSettled = async () => {
        // NO hay tope de vueltas a proposito. Un tope que resuelve al agotarse produce
        // exactamente el fallo que este modulo existe para evitar: serializar el DOM
        // antes de tiempo y mandar un PDF vacio. Quien acota el tiempo es el timeout
        // duro del portal (`renderRouteInPortal`), que falla de forma explicita.
        for (; ;) {
            if (pending > 0) {
                // Dirigido por eventos, no por sondeo: esperar aqui evita quemar CPU
                // durante los reportes largos del paquete contable.
                await new Promise<void>((resolve) => { idleWaiters.add(resolve); });
                continue;
            }

            // Se exigen dos fronteras de tarea limpias seguidas: un `release()` puede
            // disparar un re-render cuyo efecto registre trabajo nuevo (la suscripcion
            // que llega al store y recien ahi el header pide la URL del logo), y ese
            // hueco no debe leerse como "ya no queda nada".
            await yieldToTask();
            if (pending > 0) continue;

            await yieldToTask();
            if (pending === 0) return;
        }
    };

    return { begin, whenSettled };
};

/**
 * Fuera de un portal de exportacion no hay nada que esperar: la ruta de impresion
 * normal se abre con `window.open` y la imprime el usuario. El valor por defecto es
 * inerte para que esos componentes no cambien de comportamiento.
 */
const NOOP_READINESS: ExportReadiness = {
    begin: () => () => { },
    whenSettled: async () => { },
};

const ExportReadinessContext = createContext<ExportReadiness>(NOOP_READINESS);

export const ExportReadinessProvider = ExportReadinessContext.Provider;

export const useExportReadiness = (): ExportReadiness => useContext(ExportReadinessContext);
